import { useCallback, useRef, useState } from "react"
import type { UseFormReturn } from "react-hook-form"

import { DEFAULT_LIST_PRICING_VALUES } from "../components/NewRequestDialog/pricing/constants"
import type { PricingFormValues } from "../components/NewRequestDialog/pricing/schemas"

export interface UsePricingFormsReturn {
  getDefaults: (productId: number) => PricingFormValues
  registerMethods: (productId: number, methods: UseFormReturn<PricingFormValues>) => void
  unregisterMethods: (productId: number) => void
  setMissingListPrice: (productId: number, isMissing: boolean) => void
  hasMissingListPrice: (productIds: number[]) => boolean
  validateAll: (productIds: number[]) => Promise<boolean>
  getAllValues: () => Map<number, PricingFormValues>
}

export const usePricingForms = (): UsePricingFormsReturn => {
  const methodsByProductIdRef = useRef<Map<number, UseFormReturn<PricingFormValues>>>(new Map())
  const [missingListPriceProductIds, setMissingListPriceProductIds] = useState<Set<number>>(new Set())

  const getDefaults = useCallback((): PricingFormValues => DEFAULT_LIST_PRICING_VALUES, [])

  const registerMethods = useCallback((productId: number, methods: UseFormReturn<PricingFormValues>) => {
    methodsByProductIdRef.current.set(productId, methods)
  }, [])

  const unregisterMethods = useCallback((productId: number) => {
    methodsByProductIdRef.current.delete(productId)
    setMissingListPriceProductIds((prev) => {
      if (!prev.has(productId)) return prev
      const next = new Set(prev)
      next.delete(productId)
      return next
    })
  }, [])

  const setMissingListPrice = useCallback((productId: number, isMissing: boolean) => {
    setMissingListPriceProductIds((prev) => {
      const has = prev.has(productId)
      if (isMissing === has) return prev
      const next = new Set(prev)
      if (isMissing) next.add(productId)
      else next.delete(productId)
      return next
    })
  }, [])

  const hasMissingListPrice = useCallback(
    (productIds: number[]): boolean => productIds.some((id) => missingListPriceProductIds.has(id)),
    [missingListPriceProductIds]
  )

  const validateAll = useCallback(async (productIds: number[]): Promise<boolean> => {
    const results = await Promise.all(
      productIds.map((productId) => methodsByProductIdRef.current.get(productId)?.trigger() ?? Promise.resolve(true))
    )
    return results.every(Boolean)
  }, [])

  const getAllValues = useCallback((): Map<number, PricingFormValues> => {
    const values = new Map<number, PricingFormValues>()
    methodsByProductIdRef.current.forEach((methods, productId) => {
      values.set(productId, methods.getValues())
    })
    return values
  }, [])

  return {
    getDefaults,
    registerMethods,
    unregisterMethods,
    setMissingListPrice,
    hasMissingListPrice,
    validateAll,
    getAllValues,
  }
}
